/**
 * Apresentação da Capacidade da Fábrica: textos, tons e a montagem das
 * linhas da tela e do arquivo exportado. Puro (sem React): as REGRAS moram
 * em `utils/capacidade`; aqui só se decide como cada número aparece.
 *
 * Princípios do dono (lista clean, não mural): cor só comunica estado, um
 * sinal de cor por linha; todo número vem com a régua (o que mede e quanto
 * erra), em português de marcenaria.
 */
import {
  CAMADA,
  ESCALA,
  FONTE_MEDIDA,
  MOTIVO_RITMO,
  NIVEL,
  REGUA,
  RELACAO,
  SITUACAO,
  atrasados,
  camadasDoPeriodo,
  clientesDoPeriodo,
  contarDiasUteis,
  ehDaPlanilha,
  linhaDeRealidade,
  listarPeriodos,
  obraFuturaDoPeriodo,
  ocupacaoDoPeriodo,
  produzidoDoPeriodo,
} from "../../utils/capacidade";
import { dataLocalDe, formatarDataLocal, plural } from "../../utils/dinheiro";

/** Dias úteis numa semana cheia: converte o ritmo diário para o semanal. */
export const DU_POR_SEMANA = 5;

export const ESCALAS = Object.freeze([
  { id: ESCALA.MES, rotulo: "Mês" },
  { id: ESCALA.QUINZENA, rotulo: "Quinzena" },
  { id: ESCALA.SEMANA, rotulo: "Semana" },
  { id: ESCALA.DIA, rotulo: "Dia" },
]);

export const ABAS = Object.freeze([
  { id: "ocupacao", rotulo: "Ocupação" },
  // Todos com `:view` leem; registrar, ligar e cancelar dependem da permissão.
  { id: "vendas", rotulo: "Vendas assinadas" },
  { id: "qualidade", rotulo: "Qualidade" },
  { id: "precisao", rotulo: "Precisão" },
]);

export const ABA_EXCECOES = "excecoes";

/** Abas visíveis: "Exceções" só para quem marca (o PCP, decisão 9). */
export function abasDaPagina({ podeMarcarExcecao = false } = {}) {
  return podeMarcarExcecao ? [...ABAS, { id: ABA_EXCECOES, rotulo: "Exceções" }] : ABAS;
}

export const ROTULO_CAMADA = Object.freeze({
  [CAMADA.EM_PRODUCAO]: "Em produção",
  [CAMADA.APROVADO]: "Aprovado",
  [CAMADA.AGUARDANDO_APROVACAO]: "Aguardando aprovação",
  [CAMADA.OBRA_FUTURA]: "Obra futura",
  [CAMADA.VENDA_ASSINADA]: "Venda assinada",
});

export const TOM_DO_NIVEL = Object.freeze({
  [NIVEL.CRITICO]: "danger",
  [NIVEL.AVISO]: "warning",
  [NIVEL.INFO]: "neutral",
});

export const ROTULO_DO_NIVEL = Object.freeze({
  [NIVEL.CRITICO]: "Crítico",
  [NIVEL.AVISO]: "Aviso",
  [NIVEL.INFO]: "Informação",
});

const NUMERO_1_CASA = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const OPCOES_DATA_HORA = { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" };

// ------------------------------------------------------------ formatação

/** "2026-10-07" → "07/10". */
export function ddmm(iso) {
  if (!iso) return "—";
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

/** "2026-10-07" → "07/10/2026". */
export function dataCompleta(iso) {
  return iso ? formatarDataLocal(iso) : "—";
}

export function umaCasa(numero) {
  return Number.isFinite(numero) ? NUMERO_1_CASA.format(numero) : "—";
}

/** −0,08 → "−8%"; 0,19 → "+19%" (sinal sempre escrito: é erro, não valor). */
export function percentualComSinal(fracao) {
  if (!Number.isFinite(fracao)) return "—";
  const pct = Math.round(fracao * 100);
  if (pct === 0) return "0%";
  return pct > 0 ? `+${pct}%` : `−${Math.abs(pct)}%`;
}

/** ISO com fuso → "07/10 14:03" no horário do aparelho. */
export function formatarVersaoPlanilha(iso) {
  const data = dataLocalDe(iso);
  if (!data) return null;
  return data.toLocaleString("pt-BR", OPCOES_DATA_HORA).replace(",", "");
}

export function textoFaixaDePrecisao(precisao) {
  if (!precisao) return "—";
  return `${percentualComSinal(precisao.erroMin)} a ${percentualComSinal(precisao.erroMax)}`;
}

export function textoFonteDaPrecisao(precisao) {
  if (!precisao) return "";
  if (precisao.fonte === FONTE_MEDIDA) {
    return `medida em ${plural(precisao.n, "cópia guardada", "cópias guardadas")} da planilha`;
  }
  return precisao.fonte;
}

/** "07/10 a 21/10" (ou uma data só quando a faixa fecha num dia). */
export function textoFaixaDeDatas(faixa) {
  if (!faixa?.de) return "—";
  return faixa.de === faixa.ate ? ddmm(faixa.de) : `${ddmm(faixa.de)} a ${ddmm(faixa.ate)}`;
}

// ------------------------------------------------------------ motivos

/** Por que não há ritmo (e, portanto, nem ocupação nem próxima vaga). */
export function textoMotivoSemRitmo(ritmo) {
  if (!ritmo || ritmo.ritmo) return null;
  if (ritmo.motivo === MOTIVO_RITMO.CALENDARIO_AUSENTE) {
    const anos = (ritmo.anos || []).join(", ");
    return (
      `Falta cadastrar os feriados e recessos de ${anos} em Configurações > Calendário. ` +
      "Sem eles não dá para contar os dias úteis."
    );
  }
  const cobertura = Math.round((ritmo.cobertura || 0) * 100);
  return (
    `O histórico guardado cobre ${cobertura}% das 26 semanas usadas para medir o ritmo (o mínimo é 90%). ` +
    "Importe cópias antigas da planilha na aba Precisão."
  );
}

// ------------------------------------------------------------ indicadores

// O motivo completo fica no aviso logo abaixo da faixa; repetir em cada
// indicador só faria a mesma frase aparecer três vezes.
const SEM_RITMO = "Sem ritmo medido: veja o aviso abaixo.";

// D = 0 (fila vazia com ritmo medido) é "0,0 semanas", não "sem cálculo".
function semanasDaFila(fila) {
  return Number.isFinite(fila?.D) ? fila.D / DU_POR_SEMANA : null;
}

const pctInteiro = (fracao) => `${Math.round((fracao || 0) * 100)}%`;

/**
 * Leitura com as exceções marcadas (plano 5.3), enquanto ela é só a segunda
 * leitura: "Com as exceções marcadas: X semanas", com a cobertura das marcas.
 * `null` quando não há o que dizer (sem ritmo, sem nenhuma marca, ou já é a
 * conta oficial — aí a régua do indicador Fila é que diz).
 */
export function textoSegundaLeitura(painel) {
  const segunda = painel?.segundaLeitura;
  if (!segunda?.disponivel || segunda.oficial) return null;
  if (!segunda.cobertura?.marcados && !segunda.marcadosNaFila) return null;
  const semanas = semanasDaFila(segunda.projecao);
  return (
    `Com as exceções marcadas: ${umaCasa(semanas)} semanas. Só ${pctInteiro(segunda.cobertura.fracao)} dos ` +
    "ambientes terminados usados no ritmo têm marca; a conta com os pesos (leve 0,5 · pesado 2) passa a ser " +
    "a oficial quando chegar a 90%."
  );
}

function contarProblemas(qualidade) {
  const itens = (qualidade?.itens || []).filter((i) => i.nivel !== NIVEL.INFO);
  return {
    total: itens.reduce((soma, i) => soma + i.quantidade, 0),
    criticos: itens.filter((i) => i.nivel === NIVEL.CRITICO).reduce((soma, i) => soma + i.quantidade, 0),
  };
}

/** " Inclui 3 ambientes de vendas assinadas que ainda não estão na planilha." (ou ""). */
function textoDasVendasNaFila(fila) {
  const n = (fila?.itens || []).filter((item) => item.camada === CAMADA.VENDA_ASSINADA).length;
  if (n === 0) return "";
  return ` Inclui ${plural(n, "ambiente", "ambientes")} de vendas assinadas que ainda não estão na planilha.`;
}

/**
 * Faixa de indicadores do topo: `[{id, rotulo, valor, complemento, regua, tom}]`.
 * `tom` só quando o número pede atenção (deriva do ritmo, problema crítico).
 */
export function montarIndicadores(painel) {
  const { ritmo, fila, precisao, qualidade } = painel;
  const erro = `erro ${textoFaixaDePrecisao(precisao)}`;
  const problemas = contarProblemas(qualidade);
  const semanas = semanasDaFila(fila);
  const deriva = ritmo?.avisoDeriva
    ? `últimas 8 semanas: ${percentualComSinal(ritmo.deriva)}`
    : null;
  return [
    {
      id: "proxima_vaga",
      rotulo: "Próxima vaga",
      valor: ritmo?.ritmo ? textoFaixaDeDatas(fila.proximaVaga) : "—",
      complemento: null,
      regua: ritmo?.ritmo
        ? `Quando a fila de hoje termina no ritmo medido (${erro}).`
        : SEM_RITMO,
      tom: null,
    },
    {
      id: "fila",
      rotulo: "Fila",
      valor: semanas !== null ? `${umaCasa(semanas)} semanas` : "—",
      complemento: plural(fila?.fila || 0, "ambiente", "ambientes"),
      regua:
        (painel.regua === REGUA.PONDERADA
          ? "Em produção, aprovados e aguardando aprovação, com o peso das exceções marcadas (leve 0,5 · " +
            `pesado 2; ${pctInteiro(painel.segundaLeitura?.cobertura?.fracao)} dos terminados usados no ritmo ` +
            "têm marca); obra futura fica fora."
          : "Em produção, aprovados e aguardando aprovação; obra futura fica fora.") + textoDasVendasNaFila(fila),
      tom: null,
    },
    {
      id: "ritmo",
      rotulo: "Ritmo",
      valor: ritmo?.ritmo ? `${umaCasa(ritmo.ritmo * DU_POR_SEMANA)} por semana` : "—",
      complemento: deriva,
      regua: ritmo?.ritmo
        ? "Ambientes terminados nas 26 semanas fechadas (as 2 últimas ficam de fora: o término é lançado com atraso)."
        : SEM_RITMO,
      tom: deriva ? "warning" : null,
    },
    {
      id: "qualidade",
      rotulo: "Qualidade",
      valor: String(problemas.total),
      complemento: problemas.criticos ? plural(problemas.criticos, "crítico", "críticos") : null,
      regua: "Problemas na planilha que afetam a conta (aba Qualidade).",
      tom: problemas.criticos ? "danger" : null,
    },
  ];
}

// ------------------------------------------------------------ períodos

/**
 * Situação da ocupação: `{texto, tom}`. A cor vem do PIOR caso da faixa;
 * com a planilha incompleta ou parada a ocupação fica cinza, porque o
 * número existe mas não dá para confiar nele.
 */
export function estadoDaOcupacao(ocupacao, qualidade) {
  if (!ocupacao) return { texto: "—", tom: null };
  if (ocupacao.semDiaUtil) return { texto: "Sem dia útil", tom: "neutral" };
  if (qualidade?.incompleto) return { texto: `Incompleto (${qualidade.percentualCritico}%)`, tom: "neutral" };
  if (qualidade?.planilhaParada) return { texto: "Planilha parada", tom: "neutral" };
  return { texto: ocupacao.faixa.rotulo, tom: ocupacao.faixa.tom };
}

export function textoProduzido(produzido) {
  if (!produzido) return null;
  const partes = [`${plural(produzido.realizado, "terminado", "terminados")}${produzido.corrente ? " até hoje" : ""}`];
  if (!produzido.corrente && produzido.esperado) {
    partes.push(`esperado ${Math.round(produzido.esperado)} (${produzido.pct}%)`);
  }
  if (produzido.provisorio) partes.push("provisório");
  return partes.join(" · ");
}

function linhaDoPeriodo(periodo, contexto) {
  const { painel, hoje, naoUteis, anosComRegistro, escala } = contexto;
  const passado = periodo.relacao === RELACAO.PASSADO;
  const ocupacao = passado ? null : ocupacaoDoPeriodo(painel.fila, periodo);
  const produzido = produzidoDoPeriodo({ periodo, terminos: painel.terminos, naoUteis, hoje, anosComRegistro });
  return {
    ...periodo,
    diasUteis: contarDiasUteis(periodo.inicio, periodo.fim, naoUteis),
    ocupacao,
    estado: estadoDaOcupacao(ocupacao, painel.qualidade),
    produzido,
    clientes: passado ? [] : clientesDoPeriodo(painel.fila, periodo),
    camadas: passado ? null : camadasDoPeriodo(painel.fila, periodo),
    obraFutura: passado ? [] : obraFuturaDoPeriodo(painel.abertos, periodo),
    // "Compromissos da planilha": a venda assinada ainda não foi digitada,
    // então fica fora desta linha (ela já pesa na ocupação acima).
    realidade:
      !passado && escala === ESCALA.MES
        ? linhaDeRealidade({
            abertos: painel.abertos.filter(ehDaPlanilha),
            ritmo: painel.ritmo.ritmo,
            naoUteis,
            hoje,
            mes: periodo.inicio,
          })
        : null,
  };
}

/**
 * Períodos da escala em dois blocos: `proximos` (corrente + seguintes, em
 * ordem) e `produzidos` (anteriores, do mais recente para o mais antigo).
 */
export function montarPeriodos({ painel, escala, hoje, naoUteis, anosComRegistro }) {
  if (escala === ESCALA.DIA) return { proximos: [], produzidos: [] };
  const contexto = { painel, hoje, naoUteis, anosComRegistro, escala };
  const linhas = listarPeriodos(escala, hoje).map((periodo) => linhaDoPeriodo(periodo, contexto));
  return {
    proximos: linhas.filter((l) => l.relacao !== RELACAO.PASSADO),
    produzidos: linhas.filter((l) => l.relacao === RELACAO.PASSADO).reverse(),
  };
}

// ------------------------------------------------------------ dia

function pedidoEAmbienteDaChave(chave) {
  const [pedido, ...resto] = String(chave || "").split(":");
  return { pedido: pedido || "", ambiente: resto.join(":") };
}

/**
 * Visão de um dia (decisão 6: só a lista, sem %): ambientes com entrega
 * naquele dia (inclusive obra futura, marcada) e os terminados naquele dia
 * (da planilha atual ou do histórico guardado).
 */
export function montarDia(painel, dia) {
  const entregas = (painel.abertos || []).filter((linha) => linha.entrega === dia);
  const doExtrato = new Map(
    (painel.classificacao?.linhas || [])
      .filter((linha) => linha.identidade)
      .map((linha) => [linha.identidade, linha]),
  );
  const terminados = (painel.terminos || [])
    .filter((termino) => termino.termino === dia)
    .map((termino) => {
      const linha = doExtrato.get(termino.identidade);
      if (linha && linha.situacao === SITUACAO.TERMINADO) return linha;
      return { ...pedidoEAmbienteDaChave(termino.chave), chave: termino.chave, cliente: termino.cliente, termino: dia };
    });
  return { entregas, terminados };
}

// ------------------------------------------------------------ exportação

function linhasDePeriodoParaArquivo(linhas, rotuloBloco) {
  return linhas.map((linha) => ({
    bloco: rotuloBloco,
    periodo: linha.rotulo,
    diasUteis: linha.diasUteis,
    ocupacao: linha.ocupacao && !linha.ocupacao.semDiaUtil ? linha.ocupacao.texto : "",
    situacao: linha.estado.texto === "—" ? "" : linha.estado.texto,
    produzido: textoProduzido(linha.produzido) || "",
    realidade: linha.realidade?.texto || "",
  }));
}

/**
 * `{resumo, secoes}` do botão Exportar e do agendador. O arquivo leva a
 * régua de cada número (a mesma da tela), para não circular um percentual
 * sem dizer quanto ele erra.
 */
export function montarExportacao({ painel, periodos, hoje }) {
  const indicadores = montarIndicadores(painel);
  const resumo = [
    ...indicadores.map((i) => ({
      label: i.rotulo,
      value: [i.valor, i.complemento].filter(Boolean).join(" · "),
      type: "text",
    })),
    { label: "Precisão usada", value: `${textoFaixaDePrecisao(painel.precisao)} (${textoFonteDaPrecisao(painel.precisao)})`, type: "text" },
    { label: "Calculado em", value: hoje, type: "date" },
  ];
  const motivo = textoMotivoSemRitmo(painel.ritmo);
  if (motivo) resumo.push({ label: "Aviso", value: motivo, type: "text" });
  const segundaLeitura = textoSegundaLeitura(painel);
  if (segundaLeitura) resumo.push({ label: "Segunda leitura", value: segundaLeitura, type: "text" });

  const secoes = [
    {
      id: "periodos",
      label: "Ocupação e produzido por período",
      columns: [
        { key: "bloco", label: "Bloco", type: "text" },
        { key: "periodo", label: "Período", type: "text" },
        { key: "diasUteis", label: "Dias úteis", type: "number" },
        { key: "ocupacao", label: "Ocupação", type: "text" },
        { key: "situacao", label: "Situação", type: "status" },
        { key: "produzido", label: "Produzido", type: "text" },
        { key: "realidade", label: "Compromissos da planilha", type: "text" },
      ],
      rows: [
        ...linhasDePeriodoParaArquivo(periodos.proximos, "Agora e próximos"),
        ...linhasDePeriodoParaArquivo(periodos.produzidos, "Produzido"),
      ],
    },
    {
      id: "fila",
      label: "Fila projetada (posição indicativa)",
      columns: [
        { key: "posicao", label: "Posição", type: "number" },
        { key: "pedido", label: "Pedido", type: "text" },
        { key: "cliente", label: "Cliente", type: "text" },
        { key: "ambiente", label: "Ambiente", type: "text" },
        { key: "camada", label: "Situação", type: "text" },
        { key: "entrega", label: "Entrega", type: "date" },
        { key: "diaProjetado", label: "Sai por volta de", type: "date" },
        { key: "podeAtrasar", label: "Pode atrasar", type: "text" },
      ],
      rows: (painel.fila?.itens || []).map((item) => ({
        posicao: item.posicao,
        pedido: item.pedido,
        cliente: item.cliente,
        ambiente: item.ambiente,
        camada: ROTULO_CAMADA[item.camada] || "",
        entrega: item.entrega,
        diaProjetado: item.diaProjetado,
        podeAtrasar: item.podeAtrasar ? "sim" : "",
      })),
    },
    {
      id: "atrasados",
      label: "Atrasados",
      columns: [
        { key: "pedido", label: "Pedido", type: "text" },
        { key: "cliente", label: "Cliente", type: "text" },
        { key: "ambiente", label: "Ambiente", type: "text" },
        { key: "camada", label: "Situação", type: "text" },
        { key: "entrega", label: "Entrega", type: "date" },
      ],
      rows: atrasados(painel.abertos, hoje).map((item) => ({
        pedido: item.pedido,
        cliente: item.cliente,
        ambiente: item.ambiente,
        camada: ROTULO_CAMADA[item.camada] || "",
        entrega: item.entrega,
      })),
    },
    {
      id: "qualidade",
      label: "Qualidade da planilha",
      columns: [
        { key: "nivel", label: "Nível", type: "status" },
        { key: "problema", label: "Problema", type: "text" },
        { key: "quantidade", label: "Quantidade", type: "number" },
      ],
      rows: (painel.qualidade?.itens || []).map((item) => ({
        nivel: ROTULO_DO_NIVEL[item.nivel],
        problema: item.rotulo,
        quantidade: item.quantidade,
      })),
    },
  ];
  return { resumo, secoes };
}
